import React, { useState, useEffect } from 'react';
import { 
  Ambulance, Search, Eye, Trash2, 
  CheckCircle, XCircle, AlertCircle, MapPin, 
  Clock, Star, TrendingUp, Info, Shield
} from 'lucide-react';
import axios from 'axios';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';

const AmbulanceManagement = () => {
  const {
    canViewAmbulances,
    canApproveAmbulances,
    canDeleteAmbulances,
    canToggleAmbulanceStatus,
    isSuperAdmin,
    isAdmin
  } = usePermissions();
  
  const [ambulances, setAmbulances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [filters, setFilters] = useState({
    search: '',
    verificationStatus: '',
    isVerified: '',
    page: 1,
    limit: 10
  });
  
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    page: 1
  });
  
  const [selectedAmbulance, setSelectedAmbulance] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchAmbulances();
  }, [filters]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('medigo_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  const fetchAmbulances = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      
      const response = await axios.get(
        `${API_URL}/admin/ambulances?${params}`,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : [];
        setAmbulances(data);
        
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else {
          setPagination({
            total: data.length,
            pages: Math.ceil(data.length / filters.limit),
            page: filters.page
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch ambulances');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!canApproveAmbulances) {
      setError('You do not have permission to approve ambulances');
      return;
    }

    if (!window.confirm('Are you sure you want to approve this ambulance service?')) return;
    
    try {
      const response = await axios.put(
        `${API_URL}/admin/ambulances/${id}/approve`,
        {},
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Ambulance service approved successfully');
        fetchAmbulances();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve ambulance');
    }
  };

  const handleReject = async () => {
    if (!canApproveAmbulances) {
      setError('You do not have permission to reject ambulances');
      return;
    }

    if (!selectedAmbulance || !rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    
    try {
      const response = await axios.put(
        `${API_URL}/admin/ambulances/${selectedAmbulance._id}/reject`,
        { reason: rejectReason },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        setSuccess('Ambulance service rejected');
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedAmbulance(null);
        fetchAmbulances();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject ambulance');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    if (!canToggleAmbulanceStatus) {
      setError('You do not have permission to change ambulance status');
      return;
    }

    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this ambulance service?`)) return;
    
    try {
      const response = await axios.put(
        `${API_URL}/admin/ambulances/${id}/status`,
        {},
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess(response.data.message);
        fetchAmbulances();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!canDeleteAmbulances) {
      setError('Only Super Admins can delete ambulance services');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this ambulance service? This action cannot be undone.')) return;
    
    try {
      const response = await axios.delete(
        `${API_URL}/admin/ambulances/${id}`,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Ambulance service deleted successfully');
        fetchAmbulances();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete ambulance');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      verified: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.pending;
  };

  if (!canViewAmbulances) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Ambulance className="w-8 h-8 text-red-600" />
            Ambulance Management
            {isSuperAdmin && <Shield className="w-6 h-6 text-purple-600" />}
          </h1>
          <p className="text-gray-600 mt-1">Manage and verify ambulance services</p>
        </div>
      </div>

      {isSuperAdmin ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Super Admin Privileges Active</p>
            <p className="text-purple-700 text-sm">
              You can approve, verify, and delete ambulance services
            </p>
          </div>
        </div>
      ) : isAdmin ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Admin Access</p>
            <p className="text-blue-700 text-sm">
              You can approve and verify ambulances, but cannot delete them
            </p>
          </div>
        </div>
      ) : null}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-800">{success}</p>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">×</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Services</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.total || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Ambulance className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Verified</p>
              <p className="text-2xl font-bold text-green-600">
                {ambulances.filter(a => a.isVerified).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {ambulances.filter(a => a.verificationStatus === 'pending').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-blue-600">
                {ambulances.filter(a => a.isActive).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, phone, or location..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <select
            value={filters.verificationStatus}
            onChange={(e) => setFilters({ ...filters, verificationStatus: e.target.value, page: 1 })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={filters.isVerified}
            onChange={(e) => setFilters({ ...filters, isVerified: e.target.value, page: 1 })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="">All Verification</option>
            <option value="true">Verified</option>
            <option value="false">Not Verified</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        ) : ambulances.length === 0 ? (
          <div className="text-center py-12">
            <Ambulance className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No ambulance services found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ambulances.map((ambulance) => (
                  <tr key={ambulance._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{ambulance.name}</p>
                        <p className="text-sm text-gray-600">{ambulance.user?.name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">{ambulance.contact?.phone}</p>
                        <p className="text-gray-600">{ambulance.contact?.email || 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {ambulance.location?.address || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(ambulance.verificationStatus)}`}>
                          {ambulance.verificationStatus || 'pending'}
                        </span>
                        {ambulance.isActive && (
                          <span className="block text-xs text-green-600">● Active</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="text-sm text-gray-900">
                          {ambulance.rating?.average?.toFixed(1) || '0.0'}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({ambulance.rating?.totalReviews || 0})
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedAmbulance(ambulance);
                            setShowDetailsModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {canApproveAmbulances && ambulance.verificationStatus === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(ambulance._id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAmbulance(ambulance);
                                setShowRejectModal(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {canToggleAmbulanceStatus && (
                          <button
                            onClick={() => handleToggleStatus(ambulance._id, ambulance.isActive)}
                            className={`p-2 rounded-lg transition-colors ${
                              ambulance.isActive 
                                ? 'text-orange-600 hover:bg-orange-50' 
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={ambulance.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </button>
                        )}

                        {canDeleteAmbulances ? (
                          <button
                            onClick={() => handleDelete(ambulance._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete (Super Admin Only)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <span
                            className="p-2 text-gray-400 cursor-not-allowed"
                            title="Only Super Admins can delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between bg-white px-6 py-3 rounded-lg shadow">
          <div className="text-sm text-gray-700">
            Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page === pagination.pages}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showDetailsModal && selectedAmbulance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Ambulance Details</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedAmbulance(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Service Name</label>
                  <p className="mt-1 text-gray-900">{selectedAmbulance.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Owner</label>
                  <p className="mt-1 text-gray-900">{selectedAmbulance.user?.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <p className="mt-1 text-gray-900">{selectedAmbulance.contact?.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-gray-900">{selectedAmbulance.contact?.email || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">Address</label>
                  <p className="mt-1 text-gray-900">{selectedAmbulance.location?.address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Subscription Plan</label>
                  <p className="mt-1 text-gray-900 capitalize">{selectedAmbulance.subscription?.plan || 'free'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Rating</label>
                  <p className="mt-1 text-gray-900">
                    {selectedAmbulance.rating?.average?.toFixed(1) || '0.0'} / 5.0 ({selectedAmbulance.rating?.totalReviews || 0} reviews)
                  </p>
                </div>
              </div>

              {selectedAmbulance.description && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-gray-900">{selectedAmbulance.description}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Statistics</label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Total Trips</p>
                    <p className="text-xl font-bold text-gray-900">{selectedAmbulance.stats?.totalTrips || 0}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Completed</p>
                    <p className="text-xl font-bold text-green-900">{selectedAmbulance.stats?.completedTrips || 0}</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Cancelled</p>
                    <p className="text-xl font-bold text-red-900">{selectedAmbulance.stats?.cancelledTrips || 0}</p>
                  </div>
                </div>
              </div>

              {selectedAmbulance.verificationNotes && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <label className="text-sm font-medium text-gray-700 block mb-1">Verification Notes</label>
                  <p className="text-gray-900">{selectedAmbulance.verificationNotes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRejectModal && selectedAmbulance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Reject Ambulance Service</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-gray-600">
                You are about to reject <strong>{selectedAmbulance.name}</strong>. 
                Please provide a reason for rejection.
              </p>
              
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                    setSelectedAmbulance(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject Service
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbulanceManagement;